// Marketplace Item Detail Component
// Feature: 006-marketplace-management

import { useTranslation } from 'react-i18next';
import {
  MapPin,
  Calendar,
  User,
  Tag,
  Image,
  AlertCircle,
  Clock,
} from 'lucide-react';
import { Edit } from 'lucide-react';
import type { MarketplaceItemWithSeller, CategoryOption, MarketplaceItemUpdate } from '@/types/marketplaceAdmin';
import { MarketplaceStatusBadge } from './MarketplaceStatusBadge';
import { MarketplacePhotoGallery } from './MarketplacePhotoGallery';
import { MarketplaceActions } from './MarketplaceActions';
import { MarketplaceEditForm } from './MarketplaceEditForm';

interface MarketplaceDetailProps {
  item: MarketplaceItemWithSeller;
  categories: CategoryOption[];
  isEditing: boolean;
  onDelete: (reason: string) => Promise<{ success: boolean; error?: string }>;
  onRestore: () => Promise<{ success: boolean; error?: string }>;
  onExpire: () => Promise<{ success: boolean; error?: string }>;
  onReactivate: () => Promise<{ success: boolean; error?: string }>;
  onEdit: () => void;
  onCancelEdit: () => void;
  onSaveEdit: (updates: MarketplaceItemUpdate, newPhotos: File[], removedPhotoUrls: string[]) => Promise<{ success: boolean; error?: string }>;
}

export function MarketplaceDetail({
  item,
  categories,
  isEditing,
  onDelete,
  onRestore,
  onExpire,
  onReactivate,
  onEdit,
  onCancelEdit,
  onSaveEdit,
}: MarketplaceDetailProps) {
  const { t, i18n } = useTranslation('marketplaceAdmin');

  // If in edit mode, show edit form
  if (isEditing) {
    return (
      <MarketplaceEditForm
        item={item}
        categories={categories}
        onSave={onSaveEdit}
        onCancel={onCancelEdit}
      />
    );
  }

  // Normalize language to base code (en-GB -> en, km-KH -> km)
  const getBaseLanguage = () => {
    const lang = i18n.language.split('-')[0];
    return (lang === 'km' ? 'km' : 'en') as 'en' | 'km';
  };

  const lang = getBaseLanguage();

  const formatDate = (dateString: string) => {
    const date = new Date(dateString);
    const locale = lang === 'km' ? 'km-KH' : 'en-US';
    return new Intl.DateTimeFormat(locale, {
      year: 'numeric',
      month: 'long',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
    }).format(date);
  };

  const formatPrice = (price: number) => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD',
    }).format(price);
  };

  // Check if item is expiring soon (within 7 days)
  const isExpiringSoon = () => {
    if (!item.expires_at) return false;
    const expiresAt = new Date(item.expires_at);
    const now = new Date();
    const daysUntilExpiry = Math.ceil((expiresAt.getTime() - now.getTime()) / (1000 * 60 * 60 * 24));
    return daysUntilExpiry > 0 && daysUntilExpiry <= 7;
  };

  // Find category name
  const getCategoryName = () => {
    const category = categories.find(c => c.id === item.category_id);
    return category ? category.name : 'Uncategorized';
  };

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-start justify-between pb-4 border-b border-neutral-200">
        <div>
          <h2 className="text-2xl font-bold text-neutral-900 mb-2">
            {item.data.title}
          </h2>
          <div className="flex items-center gap-3">
            <MarketplaceStatusBadge status={item.data.status} />
            {isExpiringSoon() && item.data.status === 'active' && (
              <span className="inline-flex items-center gap-1 px-2 py-1 bg-orange-100 text-orange-700 rounded-full text-xs font-medium">
                <AlertCircle size={12} />
                {t('list.expiresSoon')}
              </span>
            )}
          </div>
        </div>
        <div className="flex gap-2">
          <MarketplaceActions
            currentStatus={item.data.status}
            onDelete={onDelete}
            onRestore={onRestore}
            onExpire={onExpire}
            onReactivate={onReactivate}
          />
          <button
            onClick={onEdit}
            className="flex items-center gap-2 px-4 py-2 border border-neutral-300 rounded-lg hover:bg-neutral-50 transition"
          >
            <Edit size={18} />
            {t('actions.edit')}
          </button>
        </div>
      </div>

      {/* Price Card */}
      <div className="bg-primary-50 border border-primary-200 rounded-lg p-5">
        <div className="flex items-center justify-between">
          <div>
            <p className="text-xs font-medium text-primary-600 uppercase tracking-wide mb-1">
              {t('details.price')}
            </p>
            <p className="text-3xl font-bold text-primary-700">
              {formatPrice(item.data.price)}
            </p>
          </div>
          {item.data.offer_enabled && (
            <div className="text-right">
              <p className="text-xs font-medium text-primary-600 uppercase tracking-wide mb-1">
                {t('details.offerRange')}
              </p>
              <p className="text-sm text-primary-600">
                {formatPrice(item.data.offer_min || 0)} - {formatPrice(item.data.offer_max || item.data.price)}
              </p>
            </div>
          )}
        </div>
        {!item.data.offer_enabled && (
          <p className="mt-2 text-sm text-primary-600">{t('details.offerDisabled')}</p>
        )}
      </div>

      {/* Description */}
      <div className="bg-neutral-50 border border-neutral-200 rounded-lg p-5">
        <h3 className="text-lg font-semibold text-neutral-900 mb-3">
          {t('details.description')}
        </h3>
        <p className="text-neutral-700 leading-relaxed whitespace-pre-wrap">
          {item.data.description}
        </p>
      </div>

      {/* Info Grid */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
        {/* Category & Tags Card */}
        <div className="bg-neutral-50 border border-neutral-200 rounded-lg p-5">
          <h3 className="text-lg font-semibold text-neutral-900 mb-4 flex items-center gap-2">
            <Tag size={20} className="text-primary-600" />
            {t('details.category')} & {t('details.tags')}
          </h3>
          <div className="space-y-4">
            <div>
              <p className="text-xs font-medium text-neutral-500 uppercase tracking-wide mb-1">
                {t('details.category')}
              </p>
              <p className="text-sm font-medium text-neutral-900">{getCategoryName()}</p>
            </div>
            {item.data.tags && item.data.tags.length > 0 ? (
              <div>
                <p className="text-xs font-medium text-neutral-500 uppercase tracking-wide mb-2">
                  {t('details.tags')}
                </p>
                <div className="flex flex-wrap gap-2">
                  {item.data.tags.map((tag, index) => (
                    <span
                      key={index}
                      className="text-xs px-2.5 py-1 bg-primary-100 text-primary-700 rounded-full font-medium"
                    >
                      {tag}
                    </span>
                  ))}
                </div>
              </div>
            ) : (
              <p className="text-sm text-neutral-500 italic">{t('details.noTags')}</p>
            )}
          </div>
        </div>

        {/* Location Card */}
        <div className="bg-neutral-50 border border-neutral-200 rounded-lg p-5">
          <h3 className="text-lg font-semibold text-neutral-900 mb-4 flex items-center gap-2">
            <MapPin size={20} className="text-primary-600" />
            {t('details.location')}
          </h3>
          {item.data.location || item.data.latitude ? (
            <div className="space-y-3">
              {item.data.location && (
                <div>
                  <p className="text-xs font-medium text-neutral-500 uppercase tracking-wide mb-1">
                    {t('details.location')}
                  </p>
                  <p className="text-sm text-neutral-900">{item.data.location}</p>
                </div>
              )}
              {item.data.latitude && item.data.longitude && (
                <div>
                  <p className="text-xs font-medium text-neutral-500 uppercase tracking-wide mb-1">
                    Coordinates
                  </p>
                  <p className="text-xs font-mono text-neutral-600">
                    {item.data.latitude.toFixed(6)}, {item.data.longitude.toFixed(6)}
                  </p>
                </div>
              )}
            </div>
          ) : (
            <p className="text-sm text-neutral-500 italic">{t('details.noLocation')}</p>
          )}
        </div>
      </div>

      {/* Seller Information Card */}
      <div className="bg-neutral-50 border border-neutral-200 rounded-lg p-5">
        <h3 className="text-lg font-semibold text-neutral-900 mb-4 flex items-center gap-2">
          <User size={20} className="text-neutral-600" />
          {t('details.seller')}
        </h3>
        <div className="flex items-center gap-2">
          <span className="text-sm text-neutral-900">{item.sellerEmail}</span>
          {item.sellerDeleted && (
            <span className="inline-flex items-center gap-1 px-2 py-1 bg-red-100 text-red-700 rounded text-xs font-medium">
              {t('details.sellerDeleted')}
            </span>
          )}
        </div>
      </div>

      {/* Photos */}
      <div>
        <h3 className="text-sm font-semibold text-neutral-700 mb-3 flex items-center gap-2">
          <Image size={18} className="text-neutral-500" />
          {t('details.photos')}
        </h3>
        <MarketplacePhotoGallery
          photos={item.data.photos || []}
          itemTitle={item.data.title}
        />
      </div>

      {/* Metadata */}
      <div className="border-t border-neutral-200 pt-4">
        <h3 className="text-sm font-semibold text-neutral-700 mb-3 flex items-center gap-2">
          <Clock size={18} className="text-neutral-500" />
          {t('details.dates')}
        </h3>
        <div className="grid grid-cols-1 sm:grid-cols-3 gap-3 text-sm">
          <div className="flex items-center gap-2">
            <Calendar size={14} className="text-neutral-400" />
            <span className="text-neutral-600">{t('details.created')}:</span>
            <span className="text-neutral-900">{formatDate(item.created_at)}</span>
          </div>
          <div className="flex items-center gap-2">
            <Calendar size={14} className="text-neutral-400" />
            <span className="text-neutral-600">{t('details.updated')}:</span>
            <span className="text-neutral-900">{formatDate(item.updated_at)}</span>
          </div>
          <div className="flex items-center gap-2">
            <Calendar size={14} className="text-neutral-400" />
            <span className="text-neutral-600">{t('details.expires')}:</span>
            <span className={`${isExpiringSoon() ? 'text-orange-600 font-medium' : 'text-neutral-900'}`}>
              {item.expires_at ? formatDate(item.expires_at) : t('details.noExpiry')}
            </span>
          </div>
        </div>
        
        {/* AI Generated indicator */}
        {item.data.ai_generated && (
          <div className="mt-3 text-xs text-neutral-500">
            <span className="font-medium">{t('details.aiGenerated')}:</span> {t('details.yes')}
          </div>
        )}
      </div>
    </div>
  );
}
